import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import authService from '../../services/authService'
import { useAuthStore } from '../../features/auth/stores/authStore'
import { cacheCurrentUser } from '../../features/profile/hooks/useCurrentUser'
import { userService } from '../../features/onboarding'
import { trackLogin, trackSignup, setUserId } from '../../shared/utils/analytics'
import { fbTrackLogin, fbTrackSignup, fbTrackLead } from '../../shared/utils/facebookPixel'
import './Auth.css'

type AuthStep = 'identifier' | 'login-otp' | 'signup-otp'
type LoginMode = 'email' | 'phone'

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
)

const AuthFlow = () => {
  const [step, setStep] = useState<AuthStep>('identifier')
  const [loginMode, setLoginMode] = useState<LoginMode>('phone')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [session, setSession] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { setTokens, checkAuth } = useAuthStore()

  // The effective username used for Cognito calls (email or +91XXXXXXXXXX)
  const getUsername = (): string => {
    if (loginMode === 'phone') {
      return `+91${phone}`
    }
    return email
  }

  // Display label for OTP screens
  const getDisplayIdentifier = (): string => {
    if (loginMode === 'phone') {
      return `+91${phone}`
    }
    return email
  }

  const isValidEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(emailValue)
  }

  const isValidPhone = (value: string): boolean => {
    // Indian mobile numbers: 10 digits, starts with 6-9
    return /^[6-9]\d{9}$/.test(value)
  }

  const handleIdentifierSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')

    const username = getUsername()

    if (loginMode === 'email') {
      if (!isValidEmail(email)) {
        setError('Please enter a valid email address')
        return
      }
    } else {
      if (!isValidPhone(phone)) {
        setError('Please enter a valid 10-digit mobile number')
        return
      }
    }

    setLoading(true)

    try {
      if (loginMode === 'email') {
        const emailCheck = await userService.checkEmailAvailability(email)
        if (!emailCheck.studentSignupAllowed) {
          setError('This email address is not allowed to access this portal. Please contact support.')
          setLoading(false)
          return
        }
      } else {
        const phoneCheck = await userService.checkPhoneAvailability(`+91${phone}`)
        if (!phoneCheck.studentSignupAllowed) {
          setError('This phone number is not allowed to access this portal. Please contact support.')
          setLoading(false)
          return
        }
      }

      const result = await authService.signInWithOTP(username)
      const challengeParams = result.challengeParameters

      if (challengeParams?.error === 'USER_NOT_FOUND') {
        // User doesn't exist → fall through to signup
        await handleSignup()
      } else if (challengeParams?.error === 'OTP_DELIVERY_FAILED') {
        setError('Failed to send verification code. Please try again.')
      } else {
        // User exists, OTP sent → show OTP input
        if (result.session) {
          setSession(result.session)
        }
        setStep('login-otp')
      }
    } catch (err) {
      const error = err as Error
      setError(error.message || 'Failed to proceed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSignup = async () => {
    try {
      const username = getUsername()
      if (loginMode === 'phone') {
        // Phone signup: formatted phone as username, no email
        await authService.signUp(username, '', null, phone)
      } else {
        // Email signup: email as username
        await authService.signUp(email, '', email, null)
      }
      setStep('signup-otp')
    } catch (err) {
      const error = err as Error
      setError(error.message || 'Failed to sign up. Please try again.')
    }
  }

  const handleLoginOtpVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const username = getUsername()
    const method = loginMode

    try {
      const result = await authService.verifyOTP(session, username, otp)
      if (result.success) {
        setTokens(result.tokens)
        checkAuth()
        trackLogin(method)
        fbTrackLogin(method)
        setUserId(username)
        await handlePostAuthNavigation()
      } else {
        if (result.session) {
          setSession(result.session)
        }
        setError(result.message || 'Invalid OTP. Please try again.')
      }
    } catch (err) {
      const error = err as Error
      setError(error.message || 'Invalid OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSignupOtpVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const username = getUsername()
    const method = loginMode

    try {
      // Step 1: Confirm signup with OTP
      await authService.confirmSignUp(username, otp)

      // Step 2: Auto-login with temp password
      try {
        const loginResult = await authService.signInWithPassword(username, authService.TEMP_PASSWORD)

        if (loginResult.success && loginResult.tokens) {
          setTokens(loginResult.tokens)
          checkAuth()
          const utmSource = localStorage.getItem('utm_source') || undefined
          trackSignup({
            signup_method: method,
            source: utmSource,
          })
          fbTrackSignup(method)
          fbTrackLead(`signup_${method}_otp`)
          setUserId(username)
          await handlePostAuthNavigation()
        } else {
          setError('Signup successful! Please login.')
          setTimeout(() => navigate('/login'), 2000)
        }
      } catch {
        console.log('Password auth failed, redirecting to login')
        setError('Signup successful! Please login using OTP.')
        setTimeout(() => navigate('/login'), 2000)
      }
    } catch (err) {
      const error = err as Error
      setError(error.message || 'Invalid OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handlePostAuthNavigation = async () => {
    const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/dashboard'

    try {
      const onboardResponse = await userService.onboardUser()
      cacheCurrentUser(onboardResponse.data)

      // Navigate based on profile completion status
      if (onboardResponse.isNewUser || !onboardResponse.profileComplete) {
        navigate('/onboarding', { replace: true, state: { redirectTo: from } })
      } else {
        navigate(from, { replace: true })
      }
    } catch (onboardErr) {
      console.warn('Onboard API call failed:', onboardErr)
      navigate(from, { replace: true })
    }
  }

  const handleResendOtp = async () => {
    setError('')
    setLoading(true)

    const username = getUsername()

    try {
      if (step === 'login-otp') {
        const result = await authService.signInWithOTP(username)
        if (result.session) {
          setSession(result.session)
          setError('OTP sent successfully!')
        }
      } else if (step === 'signup-otp') {
        await authService.resendConfirmationCode(username)
        setError('OTP sent successfully!')
      }
    } catch (err) {
      const error = err as Error
      setError(error.message || 'Failed to resend OTP.')
    } finally {
      setLoading(false)
    }
  }

  const handleChangeIdentifier = () => {
    setStep('identifier')
    setOtp('')
    setSession(null)
    setError('')
  }

  const handleGoogleLogin = () => {
    setGoogleLoading(true)
    setError('')
    const from = (location.state as { from?: { pathname?: string } })?.from?.pathname
    if (from) {
      sessionStorage.setItem('auth_redirect', from)
    }
    authService.initiateGoogleLogin()
  }

  const getTitle = () => {
    switch (step) {
      case 'login-otp':
      case 'signup-otp':
        return 'Verify OTP'
      default:
        return ''
    }
  }

  const getSubtitle = () => {
    switch (step) {
      case 'login-otp':
        return `Enter the OTP sent to your ${loginMode === 'phone' ? 'phone' : 'email'}`
      case 'signup-otp':
        return `Verify your ${loginMode === 'phone' ? 'phone number' : 'email'} to complete signup`
      default:
        return 'Sign in or create an account'
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <img src="/logo192.png" alt="My NEET Buddy" className="auth-logo" />
        {getTitle() && <h2>{getTitle()}</h2>}
        <p className="auth-subtitle">{getSubtitle()}</p>

        {error && (
          <div className={error.includes('success') ? 'success-message' : 'error-message'}>
            {error}
          </div>
        )}

        {step === 'identifier' && (
          <>
            <button
              type="button"
              className="btn-google"
              onClick={handleGoogleLogin}
              disabled={googleLoading || loading}
            >
              <GoogleIcon />
              {googleLoading ? 'Redirecting...' : 'Continue with Google'}
            </button>

            <div className="social-login-divider">
              <span>or</span>
            </div>

            <div className="auth-tabs">
              <button
                type="button"
                className={`auth-tab${loginMode === 'email' ? ' active' : ''}`}
                onClick={() => { setLoginMode('email'); setError('') }}
              >
                Email
              </button>
              <button
                type="button"
                className={`auth-tab${loginMode === 'phone' ? ' active' : ''}`}
                onClick={() => { setLoginMode('phone'); setError('') }}
              >
                Phone
              </button>
            </div>

            <form onSubmit={handleIdentifierSubmit}>
              {loginMode === 'email' ? (
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    autoFocus
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Phone Number</label>
                  <div className="phone-input-group">
                    <span className="phone-prefix">+91</span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '')
                        if (val.length <= 10) setPhone(val)
                      }}
                      placeholder="Enter 10-digit mobile number"
                      required
                      autoFocus
                      maxLength={10}
                    />
                  </div>
                </div>
              )}

              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Checking...' : 'Continue'}
              </button>
            </form>
          </>
        )}

        {step === 'login-otp' && (
          <form onSubmit={handleLoginOtpVerify}>
            <div className="otp-info">
              <p>OTP has been sent to <strong>{getDisplayIdentifier()}</strong></p>
              <button type="button" onClick={handleChangeIdentifier} className="btn-link">
                Change
              </button>
            </div>

            <div className="form-group">
              <label>Enter OTP</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                required
                autoFocus
              />
            </div>

            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <button type="button" onClick={handleResendOtp} disabled={loading} className="btn-secondary">
              {loading ? 'Sending...' : 'Resend OTP'}
            </button>
          </form>
        )}

        {step === 'signup-otp' && (
          <form onSubmit={handleSignupOtpVerify}>
            <div className="otp-info">
              <p>OTP has been sent to <strong>{getDisplayIdentifier()}</strong></p>
              <button type="button" onClick={handleChangeIdentifier} className="btn-link">
                Change
              </button>
            </div>

            <div className="form-group">
              <label>Enter OTP</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                required
                autoFocus
              />
            </div>

            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Verifying...' : 'Verify & Create Account'}
            </button>

            <button type="button" onClick={handleResendOtp} disabled={loading} className="btn-secondary">
              {loading ? 'Sending...' : 'Resend OTP'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

export default AuthFlow
